import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	useEffect,
	useState
} from "react";
import {
	useForceRerender
} from "client/Hooks";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { HttpClient } from "client/Api/httpClient";
import {
	CultureResolvableMap,
	resolveForCurrentCulture
} from "client_legacy/ndaw/culture";

export type EmojiSkins = readonly [
	light: string | null,
	mediumLight: string | null,
	medium: string | null,
	mediumDark: string | null,
	dark: string | null,
];

export type Emoji = {
	unicode: string;
	label: string;
	tags?: readonly string[];
	skins?: EmojiSkins;
};

export enum EmojiGroup {
	SmileysAndEmotion = 1,
	PeopleAndBody = 2,
	AnimalsAndNature = 3,
	FoodAndDrink = 4,
	TravelAndPlaces = 5,
	Activities = 6,
	Objects = 7,
	Symbols = 8,
	Flags = 9
}

export type EmojisWithGroup = {
	group: EmojiGroup;
	emojis: readonly Emoji[];
};

type UseEmojiPickerDataResult = {
	state: EmojiPickerDataState;
	reload: () => void;
};

export const useEmojiPickerData = (): UseEmojiPickerDataResult => {
	const [emojiDataState, setEmojiDataState] = useState<EmojiPickerDataState>({ kind: "loading" });
	const [reload, version] = useForceRerender();

	useEffect(
		(): (() => void) | undefined => {
			hasImplicitDependencyOn(version);

			const fetchEmojiPickerData = async (signal: AbortSignal): Promise<void> => {
				setEmojiDataState({ kind: "loading" });
				try {
					const data: readonly EmojisWithGroup[] = await getEmojiData(
						resolveForCurrentCulture(mapCultureToEmojiDataFileName),
						signal
					);
					setEmojiDataState({ kind: "success", data });
				} catch (error) {
					if (isCancelledError(error)) {
						return;
					}

					console.error(error);
					setEmojiDataState({ kind: "error" });
				}
			};

			const cancellation = new AbortController();
			fetchEmojiPickerData(cancellation.signal);

			return (): void => cancellation.abort();
		},
		[version]
	);

	return {
		state: emojiDataState,
		reload
	};
};

export type EmojiPickerDataState = {
	kind: "loading";
} | {
	kind: "error";
} | {
	kind: "success";
	data: readonly EmojisWithGroup[];
};

function getEmojiData(
	filename: string,
	signal: AbortSignal
): Promise<readonly EmojisWithGroup[]> {
	return new HttpClient()
		.get<readonly EmojisWithGroup[]>(
			`/Content/Emojis/${filename}`,
			undefined,
			{
				signal,
				cache: true
			}
		);
}

const mapCultureToEmojiDataFileName: CultureResolvableMap<string> = {
	"standard": "Emojis-4539b71e.json",
	"en-GB": "Emojis-bc1d8ddd.en-GB.json",
	"nl": "Emojis-82824b2d.nl.json",
	"da": "Emojis-7b234103.da.json",
	"nb": "Emojis-242c0247.nb.json",
	"it": "Emojis-eac9fa7d.it.json",
	"sv": "Emojis-f58eb87e.sv.json",
	"es": "Emojis-f47de72f.es.json",
	"de": "Emojis-88689ef7.de.json",
	"fr": "Emojis-ca7b3293.fr.json"
};